import type { ContactResult } from "./types";
import { supabase } from "@/lib/supabase";
import type { LocationResult } from "./contactsTypes";

function norm(s: string) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function sortAndLimitContacts(
  rows: ContactResult[],
  limit = 3,
  preferredPrimaryTags: string[] = []
): ContactResult[] {
  const normTag = (x: any) => String(x || "").toLowerCase();

  const hasDirectInfo = (c: ContactResult) =>
    !!(c.phone && c.phone.trim()) ||
    !!(c.personEmail && c.personEmail.trim()) ||
    (!!c.email && c.email.trim());

  const hasAnyPreferredTag = (c: ContactResult) => {
    const set = new Set((c.topics || []).map((t) => normTag(t)));
    return preferredPrimaryTags.some((t) => set.has(normTag(t)));
  };

  const score = (c: ContactResult) => {
    let s = 0;

    if (preferredPrimaryTags.length && hasAnyPreferredTag(c)) s += 1000;

    if (hasDirectInfo(c)) s += 100;

    if (c.title && c.title.trim()) s += 10;
    if (c.scope && c.scope.trim()) s += 5;

    return s;
  };

  const sorted = [...rows].sort((a, b) => score(b) - score(a));
  let picked = sorted.slice(0, limit);

  if (!picked.some(hasDirectInfo)) {
    const firstDirect = sorted.find(hasDirectInfo);
    if (firstDirect) {
      picked = [...picked.slice(0, Math.max(0, limit - 1)), firstDirect];

      const seen = new Set<string>();
      picked = picked
        .filter((c) => {
          const key = `${c.tab}|${c.name || ""}|${c.personEmail || c.email || ""}|${c.phone || ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, limit);
    }
  }

  return picked;
}

export async function fetchSalesLocations(q: string, limit = 25) {
  if (!supabase) {
    return { ok: false as const, error: "supabase client unavailable", results: [] as LocationResult[] };
  }

  const { data, error } = await supabase.from("locations").select("id, name").order("name", { ascending: true }).limit(250);

  if (error) {
    return { ok: false as const, error: error.message, results: [] as LocationResult[] };
  }

  const rows = (data || []).map((r) => {
    const name = String((r as { name?: string }).name || "").trim();
    return {
      company: "",
      location: name,
      address: "",
      city: "",
      state: "",
      zip: "",
      salespersons: "",
      office: "",
      email: "",
    } satisfies LocationResult;
  });

  const qn = norm(q);
  const scored = rows
    .map((r) => {
      const loc = norm(r.location);
      if (!loc) return { r, s: 0 };
      if (!qn) return { r, s: 1 };
      if (qn.includes(loc)) return { r, s: 100 };
      const locWords = loc.split(" ").filter((w) => w.length >= 4);
      const hits = locWords.filter((w) => qn.includes(w)).length;
      if (locWords.length >= 2 && hits >= 2) return { r, s: 80 };
      if (hits >= 1) return { r, s: 40 };
      return { r, s: 0 };
    })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .map((x) => x.r);

  return { ok: true as const, results: scored.slice(0, limit) };
}
